import React, { useEffect, useState } from "react";
import AtlasViewer from "./atlas/AtlasViewer";
import { SYSTEMS } from "./atlas/anatomy";

// The Human Atlas (atlas/Human-Atlas) is rendered directly in the page by
// ./atlas. Its 3D model files are served from public/plugins/atlas/models.
// No patient data is sent to or read by the viewer.
export const ATLAS_SETTINGS_STORAGE_KEY = "doctolik-plugin-atlas-settings";
export const DEFAULT_ATLAS_SETTINGS = { sex: "male", language: "en", height: 560, scene: null };
const MIN_HEIGHT = 320;
const MAX_HEIGHT = 1200;
const VIEWS = ["three-quarter", "front", "side", "back"];
const SYSTEM_IDS = new Set(SYSTEMS.map((system) => system.id));
const ID_PATTERN = /^[A-Za-z0-9_.:-]{1,80}$/;

const clampHeight = (value) => Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Number(value) || DEFAULT_ATLAS_SETTINGS.height));
const safeId = (value) => typeof value === "string" && ID_PATTERN.test(value) ? value : null;

// The 3D display configured in the plugin preview (systems, view, explode,
// selection) that the Timeline section opens with by default.
function readScene(scene, sex) {
	if (!scene || typeof scene !== "object" || scene.sex !== sex || !Array.isArray(scene.visible)) return null;
	const focus = scene.focus && (safeId(scene.focus.concept) || safeId(scene.focus.part))
		? { concept: safeId(scene.focus.concept), part: safeId(scene.focus.part) }
		: null;
	return {
		sex,
		visible: scene.visible.filter((id) => SYSTEM_IDS.has(id)),
		view: VIEWS.includes(scene.view) ? scene.view : "three-quarter",
		explode: Math.min(1, Math.max(0, Number(scene.explode) || 0)),
		rotate: scene.rotate === true,
		isolate: scene.isolate === true && Boolean(focus),
		focus,
	};
}

export function readAtlasSettings() {
	try {
		const saved = JSON.parse(localStorage.getItem(ATLAS_SETTINGS_STORAGE_KEY)) || {};
		const sex = saved.sex === "female" ? "female" : "male";
		return {
			sex,
			language: saved.language === "ar" ? "ar" : "en",
			height: clampHeight(saved.height),
			scene: readScene(saved.scene, sex),
		};
	} catch {
		return DEFAULT_ATLAS_SETTINGS;
	}
}

export function AtlasPluginSettings({ onClose }) {
	const [settings, setSettings] = useState(readAtlasSettings);
	// Remount the preview only when the form's body/language selects change, not
	// when the preview itself reports a change.
	const [previewKey, setPreviewKey] = useState(0);
	const update = (field, value) => setSettings((current) => ({ ...current, [field]: value }));
	const updateViewer = (field, value) => {
		update(field, value);
		setPreviewKey((current) => current + 1);
	};
	const captureScene = ({ sex, language, ...scene }) => setSettings((current) => ({ ...current, sex, language, scene: { sex, ...scene } }));
	const save = () => {
		const next = { ...settings, height: clampHeight(settings.height), scene: readScene(settings.scene, settings.sex) };
		localStorage.setItem(ATLAS_SETTINGS_STORAGE_KEY, JSON.stringify(next));
		// The storage event only reaches other tabs; notify this one too.
		window.dispatchEvent(new StorageEvent("storage", { key: ATLAS_SETTINGS_STORAGE_KEY }));
		onClose?.();
	};
	return <>
		<div className="PluginModalBody">
			<label>Corps de référence
				<select value={settings.sex} onChange={(event) => updateViewer("sex", event.target.value)}>
					<option value="male">Homme (BodyParts3D)</option>
					<option value="female">Femme (Human Reference Atlas)</option>
				</select>
			</label>
			<label>Langue de l'atlas
				<select value={settings.language} onChange={(event) => updateViewer("language", event.target.value)}>
					<option value="en">Anglais</option>
					<option value="ar">Arabe</option>
				</select>
			</label>
			<label>Hauteur dans la timeline (px)
				<input type="number" min={MIN_HEIGHT} max={MAX_HEIGHT} step={20} value={settings.height} onChange={(event) => update("height", event.target.value)} />
			</label>
			<p className="AtlasSettingsHint">Réglez l'affichage ci-dessous (systèmes, vue, éclatement, structure sélectionnée) : il sera affiché par défaut dans la section Timeline.</p>
			<div style={{ height: 420 }}><AtlasViewer key={previewKey} initialSex={settings.sex} initialLanguage={settings.language} initialScene={settings.scene} onSceneChange={captureScene} /></div>
		</div>
		<div className="PluginModalFooter">
			<button className="Btn Btn--ghost" type="button" onClick={onClose}>Annuler</button>
			<button className="Btn" type="button" onClick={save}>Enregistrer</button>
		</div>
	</>;
}

/** `fill`: take the whole height of the parent (full-page plugin view) instead of the configured height. */
export default function AtlasPlugin({ fill = false }) {
	const [settings, setSettings] = useState(readAtlasSettings);
	useEffect(() => {
		const refresh = (event) => {
			if (!event.key || event.key === ATLAS_SETTINGS_STORAGE_KEY) setSettings(readAtlasSettings());
		};
		window.addEventListener("storage", refresh);
		return () => window.removeEventListener("storage", refresh);
	}, []);
	return <div className="AtlasPlugin" style={{ height: fill ? "100%" : settings.height }}>
		<AtlasViewer key={JSON.stringify(settings)} initialSex={settings.sex} initialLanguage={settings.language} initialScene={settings.scene} />
	</div>;
}
